import {
  Component,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpService } from "./../services/http.service";
import { StateService } from "./../services/state.service";
import { LandingAccessWorkflow } from "./landing-access-workflow";

@Component({
  selector: "app-landing",
  templateUrl: "./landing.component.html",
  styleUrls: ["./landing.component.scss"],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class LandingComponent implements OnInit {
  private readonly accessWorkflow = new LandingAccessWorkflow();
  private profileRequested = false;
  user: {
    first_name?: string;
    last_name?: string;
    roles?: { title?: string }[];
  } = {};
  showSupportTktDialog = false;

  get isLoggedIn(): boolean {
    return !!this.stateService.getToken();
  }

  get fullName(): string {
    return [this.user.first_name, this.user.last_name]
      .filter(Boolean)
      .join(" ");
  }

  get canViewAdminPanel(): boolean {
    return (
      this.user.roles?.some((role) =>
        ["admin", "zone admin", "ticket admin", "index admin"].includes(
          (role.title || "").toLowerCase(),
        ),
      ) ?? false
    );
  }
  skusAnalyzed = 4809;
  markets = 45;
  decisionConfidence = 98;
  portfolioHealthIndex = 74.2;

  get portfolioHealthScore(): number {
    return Number.isFinite(this.portfolioHealthIndex)
      ? Math.min(100, Math.max(0, this.portfolioHealthIndex))
      : 0;
  }

  showRequestAccessDialog = false;
  accessDenied = false;
  unauthorizedType: string | null = null;
  accessDeniedMessage = "";
  accessDeniedSubMessage = "";
  restrictedHomeUrl = "/home";
  showAccessDeniedDialog = false;
  supportEmail = "support.aipho@ab-inbev.com";
  requestSubmitting = false;
  requestSubmitError: string | null = null;
  requestSubmitSuccess = false;

  requestAccessForm = this.formBuilder.group({
    email: ["", [Validators.required, Validators.email]],
    zones: ["", [Validators.required]],
    countries: [[] as string[], [Validators.required]],
  });

  constructor(
    private httpService: HttpService,
    private stateService: StateService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.unauthorizedType = params.get("accessDenied");
        this.accessDenied = this.unauthorizedType !== null;
        this.showAccessDeniedDialog = this.accessDenied;
        if (this.accessDenied) {
          const messages: Record<string, [string, string]> = {
            "1": [
              "Access required",
              "Your account does not have access to AI-PHO. Modules are unavailable until access is approved.",
            ],
            "2": [
              "Access approval pending",
              "Your access request is awaiting approval. Modules will be available once your request is approved.",
            ],
            "3": [
              "Access request rejected",
              "Your request to access AI-PHO was not approved. Modules are unavailable for your account.",
            ],
            "4": [
              "Access revoked",
              "Your access to AI-PHO has been removed. Modules are unavailable for your account.",
            ],
          };
          [this.accessDeniedMessage, this.accessDeniedSubMessage] = messages[
            this.unauthorizedType ?? ""
          ] ?? [
            "Access unavailable",
            "Your account is not authorized to use AI-PHO. Modules are unavailable.",
          ];
          this.restrictedHomeUrl =
            "/home?accessDenied=" +
            encodeURIComponent(this.unauthorizedType ?? "");
          this.supportEmail =
            this.stateService.getAppDetails()?.supportMailID ||
            this.supportEmail;
          return;
        }
        if (
          this.isLoggedIn &&
          !this.profileRequested &&
          this.stateService.apiList.user.meV3
        ) {
          this.profileRequested = true;
          this.httpService
            .getMethod(this.stateService.apiList.user.meV3)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (user) => {
                this.user = user || {};
              },
              error: () => {
                this.user = {};
              },
            });
        }
      });
  }

  continueToLogin(callbackURL = "/studio", forceLogin = false): void {
    if (this.accessDenied && !forceLogin) {
      return;
    }
    if (!forceLogin && this.stateService.getToken()) {
      void this.router.navigateByUrl(callbackURL);
      return;
    }

    sessionStorage.setItem("callbackURL", callbackURL);
    window.location.replace(this.stateService.apiList.user.login);
  }

  selectModule(module: "pho" | "global-dashboard" | "pho_dtp"): void {
    if (this.accessDenied) {
      return;
    }
    sessionStorage.setItem("studioModule", module);
    this.continueToLogin();
  }

  requestAccess(): void {
    this.accessWorkflow.requestAccess(this);
  }

  openUserPage(path: string): void {
    void this.router.navigateByUrl(path);
  }

  openDocsInNewTab(): void {
    window.open("/docs", "_blank", "noopener,noreferrer");
  }

  logout(): void {
    const finishLogout = () => {
      this.stateService.removeCalledApi(this.stateService.apiList.user.logout);
      localStorage.clear();
      sessionStorage.clear();
      window.location.replace("/home");
    };
    this.httpService
      .getMethod(this.stateService.apiList.user.logout)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: finishLogout, error: finishLogout });
  }

  onRequestAccessSubmit(payload: {
    email: string;
    zone_id: string;
    countries: string[];
  }): void {
    this.accessWorkflow.onRequestAccessSubmit(
      this,
      this.httpService,
      this.stateService,
      payload,
    );
  }
}
